import React, { useState, useEffect } from 'react';
import {message } from 'antd';
import {
    getApprovalDiff,
    getRevisionContext,
    saveRevisionDraft,
    submitForApproval,
} from 'Api/apiService';
import useAuthStore from 'Store/useAuthStore';
import useJobsStore from 'Store/useJobsStore';
import { SbButton, SbSpin } from 'uiCore';
import HumanEditorBlockV2 from './HumanEditorBlockV2';
import ApproverSelectorV2 from './ApproverSelectorV2';
import { prepareBlocksFromManifest } from './SurgicalDiffUtils';
import {
    EditorHeaderStyled,

    HeaderHeadingStyled,
    HeaderSubtitleStyled,
    BlocksListStyled,
    FooterActionsStyled,
    FooterLeftStyled,
    StatusTextStyled,
    LoadingWrapperStyled,
} from './HumanEditorV2.styles';
import SbModal from 'uiCore/SbModal/SbModal';
import { BLOCK_TYPES, JD_STATUSES } from 'constants/jobs.constants';
import { generateSurgicalId, calculateNextVersion } from "Utils/formatting";

const buildMarkdownAwarePattern = (plainText) => {
    let escaped = plainText.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    escaped = escaped.replace(/\s+/g, '(?:\\*\\*)?\\s+(?:\\*\\*)?');
    escaped = escaped.replace(/(\\[.!?])([A-Za-z])/g, '$1\\s*$2');
    return new RegExp('(?:\\*\\*)?' + escaped + '(?:\\*\\*)?');
};

const findMarkdownAwareMatch = (source, plainText) => {
    if (source.includes(plainText)) return plainText;
    const match = source.match(buildMarkdownAwarePattern(plainText));
    return match ? match[0] : null;
};

const mapStringPosition = (from, to, pos) => {
    let fi = 0, ti = 0;
    while (fi < pos && fi < from.length && ti < to.length) {
        if (from[fi] === to[ti]) { fi++; ti++; }
        else if (/\s/.test(to[ti])   && !/\s/.test(from[fi])) { ti++; }
        else if (/\s/.test(from[fi]) && !/\s/.test(to[ti]))   { fi++; }
        else { fi++; ti++; }
    }
    return Math.min(ti, to.length);
};

const mapStringPositionFromEnd = (from, to, pos) => {
    if (pos === 0) return 0;
    let fi = from.length - 1, ti = to.length - 1, count = 0;
    while (count < pos && fi >= 0 && ti >= 0) {
        if (from[fi] === to[ti]) { fi--; ti--; count++; }
        else if (/\s/.test(to[ti])   && !/\s/.test(from[fi])) { ti--; }
        else if (/\s/.test(from[fi]) && !/\s/.test(to[ti]))   { fi--; }
        else { fi--; ti--; count++; }
    }
    return to.length - 1 - ti;
};

const applyEditPreservingFormatting = (formattedOriginal, plainOriginal, plainNew) => {
    const trueOriginal = formattedOriginal.replace(/\*\*/g, '');
    if (trueOriginal === plainOriginal) return plainNew;

    const markers = [];
    let plainIdx = 0;
    for (let i = 0; i < formattedOriginal.length;) {
        if (i + 1 < formattedOriginal.length && formattedOriginal[i] === '*' && formattedOriginal[i + 1] === '*') {
            markers.push(plainIdx);
            i += 2;
        } else { plainIdx++; i++; }
    }

    let prefixLen = 0;
    const minLen = Math.min(plainOriginal.length, plainNew.length);
    while (prefixLen < minLen && plainOriginal[prefixLen] === plainNew[prefixLen]) prefixLen++;

    let suffixLen = 0;
    const maxSuffix = Math.min(plainOriginal.length - prefixLen, plainNew.length - prefixLen);
    while (
        suffixLen < maxSuffix &&
        plainOriginal[plainOriginal.length - 1 - suffixLen] === plainNew[plainNew.length - 1 - suffixLen]
    ) suffixLen++;

    const trueEditStart = mapStringPosition(plainOriginal, trueOriginal, prefixLen);
    const suffixMapped  = suffixLen > 0 ? mapStringPositionFromEnd(plainOriginal, trueOriginal, suffixLen) : 0;
    const trueEditEnd   = trueOriginal.length - suffixMapped;

    const addedText = plainNew.substring(prefixLen, suffixLen > 0 ? plainNew.length - suffixLen : undefined);
    const newTrue   =
        trueOriginal.substring(0, trueEditStart) +
        addedText +
        trueOriginal.substring(trueEditEnd);

    if (markers.length === 0) return newTrue;

    const editLenDiff      = addedText.length - (trueEditEnd - trueEditStart);
    const adjustedMarkers  = [];
    for (const pos of markers) {
        if (pos <= trueEditStart)  adjustedMarkers.push(pos);
        else if (pos >= trueEditEnd) adjustedMarkers.push(pos + editLenDiff);
    }

    let rebuilt = '', mi = 0;
    for (let i = 0; i <= newTrue.length; i++) {
        while (mi < adjustedMarkers.length && adjustedMarkers[mi] === i) { rebuilt += '**'; mi++; }
        if (i < newTrue.length) rebuilt += newTrue[i];
    }
    return rebuilt;
};

const applySurgicalPatch = (sourceMarkdown, blocks) => {
    let result = sourceMarkdown || '';

    const restoredGhosts = (blocks || []).filter((b) => b.isGhost && b.type !== BLOCK_TYPES.GHOST && b.content);
    restoredGhosts.forEach((ghost) => {
        const ghostIndexInState = blocks.findIndex((b) => b.id === ghost.id);
        const anchorBlock       = blocks[ghostIndexInState - 1];
        const sep               = ghost.content.trim().startsWith('*') ? '\n' : '\n\n';

        if (anchorBlock?.originalContent) {
            const anchorMatch = findMarkdownAwareMatch(result, anchorBlock.originalContent);
            if (anchorMatch) {
                const parts = result.split(anchorMatch);
                if (parts.length > 1) {
                    result = parts[0] + anchorMatch + sep + ghost.content + sep + parts.slice(1).join(anchorMatch);
                }
            } else {
                result = ghost.content + sep + result;
            }
        } else {
            result = ghost.content + sep + result;
        }
    });

    const standardBlocks = (blocks || [])
        .filter((b) => !b.isGhost && b.originalContent)
        .filter((b) => b.content !== b.originalContent || b.isDeleted)
        .sort((a, b) => (b.originalContent?.length || 0) - (a.originalContent?.length || 0));

    standardBlocks.forEach((block) => {
        const searchString      = block.originalContent;
        const replacementString = block.isDeleted ? '' : block.content;
        if (!searchString) return;

        if (result.includes(searchString)) {
            result = result.split(searchString).join(replacementString);
        } else {
            const regex = buildMarkdownAwarePattern(searchString);
            const match = result.match(regex);
            if (match) {
                const newText = block.isDeleted
                    ? ''
                    : applyEditPreservingFormatting(match[0], searchString, block.content);
                result = result.replace(match[0], newText);
            }
        }
    });

    return result;
};

// ─────────────────────────────────────────────────────────────────────────────

const SbJdHumanEditorV2 = ({
    onCancel,
    onSave,
    selectedApprover,
    setSelectedApprover,
    assignedApproverName,
}) => {
    const email = useAuthStore((s) => s.email);
    const selectedJob = useJobsStore((state) => state.selectedJob);

    const [blocks,          setBlocks]          = useState([]);
    const [loading,         setLoading]         = useState(true);
    const [submitting,      setSubmitting]       = useState(false);
    const [showDeleteModal, setShowDeleteModal]  = useState(null);
    const [manifestData,    setManifestData]     = useState(null);
    const [revisionContext, setRevisionContext]  = useState(null);
    const [activeMenuId,    setActiveMenuId]     = useState(null);
    const [touchedIds,      setTouchedIds]       = useState(new Set());

    useEffect(() => {
        if (selectedJob?.job_id) loadInitializationData();
    }, [selectedJob?.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadInitializationData = async () => {
        try {
            setLoading(true);
            const [context, manifest] = await Promise.all([
                getRevisionContext(selectedJob?.job_id),
                getApprovalDiff(selectedJob?.job_id),
            ]);
            setRevisionContext(context);
            setManifestData(manifest);
            setBlocks(prepareBlocksFromManifest(manifest));
        } catch (err) {
            console.error('Initialization failed:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleUpdate = (id, newText) => {
        setTouchedIds((prev) => new Set(prev).add(id));
        setBlocks((prev) =>
            prev.map((b, idx) => {
                if (b.id !== id) return b;
                const isTextModified = newText.trim() !== b.originalContent.trim();
                const isMoved        = idx !== b.originalIndex;
                if (!isTextModified && !isMoved) {
                    return { ...b, content: newText, type: b.hasComment ? BLOCK_TYPES.NEEDS_WORK : BLOCK_TYPES.LOCKED };
                }
                return { ...b, content: newText, type: BLOCK_TYPES.MODIFIED };
            }),
        );
    };

    const handleMove = (id, direction) => {
        setTouchedIds((prev) => new Set(prev).add(id));
        setBlocks((prev) => {
            const idx    = prev.findIndex((b) => b.id === id);
            const newIdx = idx + direction;
            if (newIdx < 0 || newIdx >= prev.length) return prev;
            const newBlocks = [...prev];
            const [movedItem] = newBlocks.splice(idx, 1);
            newBlocks.splice(newIdx, 0, movedItem);
            return newBlocks.map((b, i) => {
                const isMoved     = i !== b.originalIndex;
                const isTextEdited = b.content.trim() !== b.originalContent.trim();
                const wasIntentionallyChanged =
                    isTextEdited || (isMoved && touchedIds.has(b.id)) || b.id === id;
                if (!wasIntentionallyChanged && !isTextEdited && !isMoved) {
                    return { ...b, type: b.hasComment ? BLOCK_TYPES.NEEDS_WORK : BLOCK_TYPES.LOCKED };
                }
                if (wasIntentionallyChanged) return { ...b, type: BLOCK_TYPES.MODIFIED };
                return b;
            });
        });
    };

    const handleRevert = (id) => {
        setTouchedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        setBlocks((prev) => {
            const targetBlock = prev.find((b) => b.id === id);
            if (!targetBlock) return prev;
            const resetBlock  = { ...targetBlock, content: targetBlock.originalContent };
            const filtered    = prev.filter((b) => b.id !== id);
            const newBlocks   = [...filtered];
            newBlocks.splice(targetBlock.originalIndex, 0, resetBlock);
            return newBlocks.map((b, i) => {
                const isTextModified = b.content.trim() !== b.originalContent.trim();
                const isMoved        = i !== b.originalIndex;
                if (!isTextModified && !isMoved) return { ...b, type: b.hasComment ? BLOCK_TYPES.NEEDS_WORK : BLOCK_TYPES.LOCKED };
                return { ...b, type: BLOCK_TYPES.MODIFIED };
            });
        });
    };

    const handleSubmit = async () => {
        if (!selectedApprover || !revisionContext?.latest_version) return;
        setSubmitting(true);
        try {
            const sourceMarkdown = revisionContext.latest_version.generated_job_desc_md || '';
            if (!sourceMarkdown) throw new Error('Source Job Description is empty or missing from backend.');

            const finalMarkdown  = applySurgicalPatch(sourceMarkdown, blocks);
            const currentVer     = parseFloat(revisionContext.latest_version.version || 1.0);
            const nextVersionNum = calculateNextVersion(currentVer);
            const newUniqueId    = generateSurgicalId();

            const bqPayload = {
                ...revisionContext.latest_version,
                sb_unique_id:          newUniqueId,
                version:               nextVersionNum,
                jd_status:             JD_STATUSES.PENDING_APPROVAL,
                user_id:               email,
                job_desc_md:           finalMarkdown,
                generated_job_desc_md: finalMarkdown,
                jd_input_type:         'human-edit',
            };

            await saveRevisionDraft(bqPayload);
            await new Promise((resolve) => setTimeout(resolve, 250));
            await submitForApproval(newUniqueId, selectedApprover);

            if (typeof onSave === 'function') onSave(newUniqueId);
            else if (typeof onCancel === 'function') onCancel();
        } catch (err) {
            console.error('Submission Failed:', err);
            message.error(`Submission failed: ${err.message}`);
        } finally {
            setSubmitting(false);
        }
    };

    const unresolvedCount = blocks.filter((b) => !b.isDeleted && (b.type === BLOCK_TYPES.NEEDS_WORK || b.type === BLOCK_TYPES.GHOST)).length;
    const canSubmit       = unresolvedCount === 0 && !!selectedApprover;

    if (loading) {
        return (
            <LoadingWrapperStyled>
                <SbSpin size="large" />
            </LoadingWrapperStyled>
        );
    }

    return (
        <>
            {/* ── Header ── */}
            <EditorHeaderStyled>
                <div>
                    <HeaderHeadingStyled>Revision Mode</HeaderHeadingStyled>
                    <HeaderSubtitleStyled>
                        Addressing feedback for JD Version - {manifestData?.current_version}
                    </HeaderSubtitleStyled>
                </div>
                <SbButton onClick={onCancel} buttonType="primary">
                    Cancel Revision
                </SbButton>
            </EditorHeaderStyled>

            {/* ── Block list ── */}
            <BlocksListStyled>
                {blocks.map((block, i) => (
                    <HumanEditorBlockV2
                        key={block.id}
                        block={block}
                        isFirst={i === 0}
                        isLast={i === blocks.length - 1}
                        onUpdate={handleUpdate}
                        onMove={handleMove}
                        onRevert={handleRevert}
                        onDelete={(id) => setShowDeleteModal(id)}
                        showActionMenu={activeMenuId === block.id}
                        onToggleMenu={() => setActiveMenuId(activeMenuId === block.id ? null : block.id)}
                        closeMenu={() => setActiveMenuId(null)}
                    />
                ))}
            </BlocksListStyled>

            {/* ── Footer ── */}
            <FooterActionsStyled>
                <FooterLeftStyled>
                    <ApproverSelectorV2
                        selectedApprover={selectedApprover}
                        setSelectedApprover={setSelectedApprover}
                        assignedApproverName={assignedApproverName || revisionContext?.previous_approver}
                    />
                        <StatusTextStyled $ok={unresolvedCount === 0}>
                            {unresolvedCount > 0
                                ? `✕ ${unresolvedCount} items remaining`
                                : '✓ All feedback addressed'}
                        </StatusTextStyled>
                </FooterLeftStyled>
                <SbButton
                    buttonType="primary"
                    size="large"
                    disabled={!canSubmit || submitting}
                    loading={submitting}
                    onClick={handleSubmit}
                >
                    Submit Revised JD
                </SbButton>
            </FooterActionsStyled>

            {/* ── Delete confirm modal ── */}
            <SbModal
                open={!!showDeleteModal}
                onCancel={() => setShowDeleteModal(null)}
                title="Confirm Delete"
                wrapClassName="he-delete-modal"
                footer={[
                    <SbButton key="cancel" onClick={() => setShowDeleteModal(null)}>
                        Cancel
                    </SbButton>,
                    <SbButton
                        color="red" 
                        variant="solid"
                        key="delete"
                        onClick={() => {
                            setBlocks((prev) =>
                                prev.map((b) =>
                                    b.id === showDeleteModal ? { ...b, isDeleted: true, content: '' } : b,
                                ),
                            );
                            setShowDeleteModal(null);
                        }}
                    >
                        Delete Block
                    </SbButton>,
                ]}
                centered
            >
                Are you sure you want to delete this block?
            </SbModal>
        </>
    );
};

export default SbJdHumanEditorV2;
